import {FilterableMessageCentreUrlSlugs} from "@twigsby/client-data";
import {
    ButtonSubmit,
    ColourSolid,
    ComboBoxControl,
    DatePickerControl,
    FlexGap,
    FormWrapperLabel,
    Modal,
    ModalType,
    StackVertical,
    useTranslation
} from "@twigsby/frontend-framework";
import {Controller, FormProvider, useForm} from "@twigsby/frontend-framework/lib/ReactHookForm";
import {FormatLocaleDate} from "@twigsby/helpers";
import React, {useEffect, useState} from "react";
import {UpdateSingleFilter} from "../../../Helpers/UrlSlugsAndFilters";
import {useMessageCentre} from "../../../Providers/MessageCentreProvider";
import {useSite} from "../../../Providers/SiteInterfaceProvider";

export const MessageRead = [
    {
        label: 'All',
        value: -1,
    },
    {
        label: 'Read',
        value: true,
    },
    {
        label: 'Unread',
        value: false,
    }
];

export const MessageArchived = [
    {
        label: 'All',
        value: -1,
    },
    {
        label: 'Archived',
        value: true,
    },
    {
        label: 'Un-archived',
        value: false,
    }
];

export const MessageAttachments = [
    {
        label: 'All',
        value: -1,
    },
    {
        label: 'Has Attachments',
        value: true,
    },
    {
        label: 'No Attachments',
        value: false,
    }
];

interface FilterMessagesModalType extends ModalType {

}

const FilterMessagesModal = (props: FilterMessagesModalType) => {
    const { t } = useTranslation();
    const { currentLocale } = useSite();

    const {
        userFilters,
        SetFilters
    } = useMessageCentre();

    const [ localFilters , setLocalFilters ] = useState<FilterableMessageCentreUrlSlugs>(userFilters);

    const formMethods = useForm();

    const {
        control,
    } = formMethods;

    const HandleSubmit = () => {
        SetFilters(localFilters);
        props.closeCallback();
    }

    useEffect(() => {
        if(props.isOpen) {
            setLocalFilters(userFilters);
        }
    }, [userFilters, props.isOpen]);

    return (
        <Modal title={t('messageCentre.filter.title')} isOpen={props.isOpen} closeCallback={props.closeCallback}>
            <FormProvider {...formMethods}>
                <form onSubmit={formMethods.handleSubmit(HandleSubmit)}>
                    <fieldset>

                        <StackVertical gap={FlexGap.Medium}>

                            <div className={'row'}>
                                <div className={'col-xs-12 col-md-6'}>
                                    <StackVertical gap={FlexGap.Medium}>
                                        <Controller
                                            name={'searchTerm'}
                                            control={control}
                                            render={({ field, fieldState}) =>
                                                <FormWrapperLabel
                                                    label={t('messageCentre.filter.labels.term')}
                                                    invalid={fieldState.invalid}
                                                    validationMessage={fieldState.error?.message}
                                                >
                                                    <input
                                                        type='text'
                                                        onChange={(e:any) => {
                                                            field.onChange(e.currentTarget?.value);
                                                            setLocalFilters((prevState) => UpdateSingleFilter(prevState, 'searchTerm', e.currentTarget?.value, t('messageCentre.filter.labels.searchTerm', { value: e.currentTarget?.value})) as FilterableMessageCentreUrlSlugs);
                                                        }}
                                                        defaultValue={localFilters && localFilters['searchTerm'] ? localFilters['searchTerm']?.value as string : undefined}
                                                    />
                                                </FormWrapperLabel>
                                            }
                                        />

                                        <Controller
                                            name={'read'}
                                            control={control}
                                            render={({ field, fieldState}) =>
                                                <FormWrapperLabel
                                                    label={t('messageCentre.filter.labels.readStatus')}
                                                    invalid={fieldState.invalid}
                                                    validationMessage={fieldState.error?.message}
                                                >
                                                    <ComboBoxControl
                                                        name={'read'}
                                                        placeholder={t('messageCentre.filter.controls.readPlaceholder')}
                                                        data={MessageRead}
                                                        defaultValues={localFilters && localFilters['read'] ? [localFilters['read'].value] : undefined}
                                                        forwardValueCallback={(e) => {
                                                            field.onChange(e);
                                                            setLocalFilters((prevState) => UpdateSingleFilter(prevState, 'read', e, e ? t('messageCentre.filter.labels.isRead') : t('messageCentre.filter.labels.isUnread')) as FilterableMessageCentreUrlSlugs);
                                                        }}
                                                        singleSelection={true}
                                                        valueType={'boolean'}
                                                    />
                                                </FormWrapperLabel>
                                            }
                                        />

                                        <Controller
                                            name={'archived'}
                                            control={control}
                                            render={({ field, fieldState}) =>
                                                <FormWrapperLabel
                                                    label={t('messageCentre.filter.labels.archived')}
                                                    invalid={fieldState.invalid}
                                                    validationMessage={fieldState.error?.message}
                                                >
                                                    <ComboBoxControl
                                                        name={'archived'}
                                                        placeholder={t('messageCentre.filter.controls.archivedPlaceholder')}
                                                        data={MessageArchived}
                                                        defaultValues={localFilters && localFilters['archived'] ? [localFilters['archived'].value] : undefined}
                                                        forwardValueCallback={(e) => {
                                                            field.onChange(e);
                                                            setLocalFilters((prevState) => UpdateSingleFilter(prevState, 'archived', e, e ? t('messageCentre.filter.labels.isArchived') : t('messageCentre.filter.labels.notArchived')) as FilterableMessageCentreUrlSlugs);
                                                        }}
                                                        singleSelection={true}
                                                        valueType={'boolean'}
                                                    />
                                                </FormWrapperLabel>
                                            }
                                        />
                                    </StackVertical>
                                </div>
                                <div className={'col-xs-12 col-md-6'}>
                                    <StackVertical gap={FlexGap.Medium}>
                                        <Controller
                                            name={'dateRange'}
                                            control={control}
                                            render={({ field, fieldState}) =>
                                                <FormWrapperLabel
                                                    label={t('messageCentre.filter.labels.dateRange')}
                                                    invalid={fieldState.invalid}
                                                    validationMessage={fieldState.error?.message}
                                                >
                                                    <DatePickerControl
                                                        name="dateRange"
                                                        maxDate={new Date()}
                                                        forwardValuesCallback={(e) => {
                                                            formMethods.setValue('startDate', e.startDate);
                                                            formMethods.setValue('endDate', e.endDate);
                                                            setLocalFilters((prevState) => UpdateSingleFilter(prevState, 'startDate', e.startDate, t('messageCentre.filter.labels.startDate', { date: FormatLocaleDate(e.startDate, currentLocale) })) as FilterableMessageCentreUrlSlugs);
                                                            setLocalFilters((prevState) => UpdateSingleFilter(prevState, 'endDate', e.endDate, t('messageCentre.filter.labels.endDate', { date: FormatLocaleDate(e.endDate, currentLocale) })) as FilterableMessageCentreUrlSlugs);
                                                        }}
                                                        defaultStartDate={localFilters && localFilters['startDate']?.value ? localFilters['startDate']?.value as Date : new Date()}
                                                        defaultEndDate={localFilters && localFilters['endDate']?.value ? localFilters['endDate']?.value as Date : new Date()}
                                                        locale={currentLocale}
                                                        moveRangeOnFirstSelection={false}
                                                    />
                                                </FormWrapperLabel>
                                            }
                                        />


                                        <Controller
                                            name={'hasAttachment'}
                                            control={control}
                                            render={({ field, fieldState}) =>
                                                <FormWrapperLabel
                                                    label={t('messageCentre.filter.labels.attachments')}
                                                    invalid={fieldState.invalid}
                                                    validationMessage={fieldState.error?.message}
                                                >
                                                    <ComboBoxControl
                                                        name={'hasAttachment'}
                                                        placeholder={t('messageCentre.filter.controls.attachmentsPlaceholder')}
                                                        data={MessageAttachments}
                                                        defaultValues={localFilters && localFilters['hasAttachment'] ? [localFilters['hasAttachment'].value] : undefined}
                                                        forwardValueCallback={(e) => {
                                                            field.onChange(e);
                                                            setLocalFilters((prevState) => UpdateSingleFilter(prevState, 'hasAttachment', e, e ? t('messageCentre.filter.labels.hasAttachments') : t('messageCentre.filter.labels.noAttachments')) as FilterableMessageCentreUrlSlugs);
                                                        }}
                                                        singleSelection={true}
                                                        valueType={'boolean'}
                                                    />
                                                </FormWrapperLabel>
                                            }
                                        />

                                    </StackVertical>
                                </div>
                            </div>

                            <ButtonSubmit
                                fullWidth={true}
                                colour={ColourSolid.Primary}
                                title={t('messageCentre.filter.actions.save')}
                            />
                        </StackVertical>

                    </fieldset>
                </form>
            </FormProvider>
        </Modal>
    )
}

export { FilterMessagesModal }
